import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Icons,
  Modal,
  Radio,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import {
  DocInfo,
  DriveFolder,
  DriveFolderInfoDTO,
  FolderInfoDTO,
  UploadFile,
} from "../../types/files";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { nanoid } from "@reduxjs/toolkit";
import { t } from "i18next";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import useAppStore from "../../redux/dispatcher/useAppStore";
interface FolderMoveModalProps {
  open: boolean;
  onClose: () => void;
  docInfo?: DocInfo[]; // 이동하고 싶은 문서들
}
const ChooseDriveFolderModal: React.FC<FolderMoveModalProps> = ({
  open,
  onClose,
  docInfo,
}) => {
  /** Hook 정리 */
  const toasts = useToast();
  const checkboxGroup = useCheckboxGroup<string>();
  const { uploadEcoToDrive, getDriveFolderList } = useAppStore();
  const { setAddUploadProgressFiles, setUpdateUploadProgressFiles } =
    useDocumentStore();

  /** State 정리 */
  const [folderData, setFolderData] = useState([] as DriveFolder[]);
  const [selectFolderId, changeSelectFolderId] = useState("");
  /** UseEffect 정리 */
  const searchFolder = async (folderId: string): Promise<DriveFolder> => {
    let resp: any = await getDriveFolderList({
      type: "dropbox",
      folder: folderId === "ROOT" ? "" : folderId,
    });
    const folderIds = folderData.map((item) => item.folderInfo?.id);
    if (resp) {
      setFolderData([
        ...folderData?.filter(
          (item) => item?.folderInfo?.id !== resp.payload.entity?.folderInfo?.id
        ),
        resp.payload?.entity,
        ...resp.payload?.entity?.childFolderList
          ?.filter(
            (item: DriveFolder) => !folderIds.includes(item?.folderInfo?.id)
          )
          .map((item: DriveFolderInfoDTO) => {
            return {
              folderInfo: item,
              childrenFolderList: [],
              parentFolderInfo: [],
            };
          }),
      ]);
    }

    return resp?.payload?.entity;
  };
  const getRootFolderModal = async (): Promise<void> => {
    const result: any = await getDriveFolderList({
      type: "dropbox",
      folder: "",
    });
    if (result) {
      setFolderData([
        result.payload?.entity,
        ...result.payload?.entity?.childFolderList?.map(
          (item: DriveFolderInfoDTO) => {
            return {
              folderInfo: item,
              childrenFolderList: [],
              parentFolderInfo: [],
            };
          }
        ),
      ]);
    }
  };
  useEffect(() => {
    getRootFolderModal();
  }, []);

  /** 함수 정리 */
  /** 문서를 대상 폴더로 이동 */
  const handleMoverFileToOtherFolder = async () => {
    if (!docInfo) return;
    let failCnt = 0;

    const uuid = nanoid();
    docInfo.forEach(async (file, index) => {
      const uploadFile: UploadFile = {
        uuid: index + uuid,
        fileName: file.contentName,
        fileSize: file.fileSize ?? 0,
        workgroupId: "",
        workgroupName: "",
        status: "WAIT", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        fileType: "DROPBOX",
      };
      setAddUploadProgressFiles(uploadFile);
    });
    docInfo.forEach(async (file, index) => {
      setUpdateUploadProgressFiles({
        uuid: index + uuid,
        fileName: file.contentName,
        fileSize: file.fileSize ?? 0,
        workgroupId: "",
        workgroupName: "",
        status: "ING", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        fileType: "DROPBOX",
      });
      const resp: any = await uploadEcoToDrive({
        type: "dropbox",
        syncId: file.syncId,
        revisionNo: file.revisionNo,
        targetFolder: selectFolderId,
      });
      setUpdateUploadProgressFiles({
        uuid: index + uuid,
        fileName: file.contentName,
        fileSize: file.fileSize ?? 0,
        workgroupId: "",
        workgroupName: "",
        status: resp.payload ? "SUCCESS" : "FAIL", // WAIT, ING, SUCCESS, FAIL
        errorMsg: resp.error ? resp.error.message : "",
        fileType: "DROPBOX",
        fileId:
          resp.payload && resp.payload.entity ? resp.payload.entity.fileId : "",
      });
      if (!resp.payload) failCnt++;
      if (index === docInfo.length - 1 && failCnt === 0) {
        toasts.toastMsg(
          nanoid(),
          t("file.alert.finishDriveInfo", {
            name: "Dropbox",
          }),
          "success"
        );
      }
    });

    onClose();
  };
  /** UI: 폴더 아이템 한개씩 */
  const renderFolderItem = (item: DriveFolderInfoDTO, depth: number) => {
    return (
      <Box
        height={60}
        minHeight={60}
        alignItems="center"
        width={"100%"}
        style={{
          boxSizing: "border-box",
          paddingLeft: `${(depth ?? 0) * 12 + 8}px`,
        }}
      >
        {!item?.hasSubFolder ? (
          <Box width={32} height={32} alignItems="center" />
        ) : (
          <Box
            width={32}
            height={32}
            alignItems="center"
            classes="cursor pointer"
            onClick={() => {
              checkboxGroup.handleClick(item?.id);
              item?.id !== "ROOT" && searchFolder(item?.id);
            }}
          >
            <Icons
              variant="line"
              label="chevron"
              direction={checkboxGroup.isSelected(item?.id) ? "down" : "right"}
              size={20}
            />
          </Box>
        )}
        <Box classes="mg ml-8" minWidth={20}>
          <Icons variant="document" label="folder" />
        </Box>

        <Tooltip
          message={item?.name}
          // maxWidth={`calc(100% - ${
          //   (item.parentFolder?.length ?? 0) * 12
          // }px - 90px)`}
        >
          <Typography type="body2_rg" ellipsis classes="mg ml-8">
            {/* {item.workgroupId === item?.id
              ? workgroupState.workgroupDetail.workgroup.workgroupName
              : item?.name} */}
            {item?.name}
          </Typography>
        </Tooltip>
        <Radio
          check={selectFolderId === item?.id}
          onClick={() => changeSelectFolderId(item?.id)}
          style={{ marginLeft: "auto" }}
        />
      </Box>
    );
  };
  /** UI: 폴더 트리 구조 */
  const renderRecursiveItem = (data: DriveFolder, depth: number) => {
    return (
      <Box direction="column">
        {renderFolderItem(data?.folderInfo, depth)}
        {checkboxGroup.isSelected(data?.folderInfo?.id) &&
          data?.childFolderList?.map((item1) =>
            renderRecursiveItem(
              folderData?.filter(
                (item) => item?.folderInfo?.id === item1.id
              )[0],
              depth + 1
            )
          )}
      </Box>
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("file.action.moveToDropbox")}
      width={600}
      bodyHeight={386}
      footer={
        <Box>
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            type="secondary"
            onClick={onClose}
          />
          <Button
            btnStyle="fill"
            label={t("common.move")}
            state={selectFolderId ? "default" : "disable"}
            onClick={handleMoverFileToOtherFolder}
          />
        </Box>
      }
      body={
        <Box
          direction="column"
          width={"100%"}
          height={"100%"}
          classes="folderMoveModal"
        >
          {renderRecursiveItem(folderData[0], 0)}
        </Box>
      }
    />
  );
};
export default ChooseDriveFolderModal;
